import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pill, Loader2, Trash2, Info } from "lucide-react";
import { EmptyState } from "@/components/consultation/EmptyState";
import {
  usePrescriptions,
  useUpdatePrescriptionItem,
  useMedicineUnits,
  useMedicationFrequencies,
  useMedicationDurations,
  useDeletePrescriptionItem,
} from "@/hooks/usePrescriptions";
import { AddMedicineDialog } from "@/components/consultation/dialogs/AddMedicineDialog";
import { useDebounce } from "@/hooks/use-debounce";
import type { PrescriptionItem } from "@/types/prescription";

interface MedicineCardProps {
  appointmentId: string;
  isCompleted?: boolean;
}

// Auto-growing textarea for advice field (Excel-like behavior)
interface AdviceTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onSave: (value: string) => void;
  disabled?: boolean;
}

const SINGLE_LINE_HEIGHT = 36; // matches h-9
const MAX_LINES = 3;
const LINE_HEIGHT = 20; // approx line-height for text-sm
const MAX_HEIGHT = SINGLE_LINE_HEIGHT + LINE_HEIGHT * (MAX_LINES - 1); // ~72px for 3 lines

const AdviceTextarea = ({
  value,
  onChange,
  onSave,
  disabled,
}: AdviceTextareaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    // Reset to single line to get accurate scrollHeight
    el.style.height = `${SINGLE_LINE_HEIGHT}px`;
    const newHeight = Math.min(el.scrollHeight, MAX_HEIGHT);
    el.style.height = `${newHeight}px`;
    // Only show scrollbar when content exceeds max height
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  return (
    <textarea
      ref={textareaRef}
      placeholder="Enter advice"
      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none scrollbar-thin"
      style={{ height: SINGLE_LINE_HEIGHT, overflowY: "hidden" }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => onSave(value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          onSave(value);
          textareaRef.current?.blur();
        }
      }}
      disabled={disabled}
      rows={1}
    />
  );
};

const PrescriptionItemRow = ({
  item,
  appointmentId,
  isCompleted = false,
}: {
  item: PrescriptionItem;
  appointmentId: string;
  isCompleted?: boolean;
}) => {
  const updateMutation = useUpdatePrescriptionItem(appointmentId);
  const deleteMutation = useDeletePrescriptionItem(appointmentId);
  const { data: units } = useMedicineUnits();
  const { data: frequencies } = useMedicationFrequencies();
  const { data: durations } = useMedicationDurations();

  const [quantity, setQuantity] = useState(item.quantity?.toString() || "0");
  const [advice, setAdvice] = useState(item.medicine_advice || "");

  const debouncedQuantity = useDebounce(quantity, 500);
  // Note: advice no longer uses debounce - it saves on blur or Enter key

  // Sync state with props if they change externally
  useEffect(() => {
    setQuantity(item.quantity?.toString() || "0");
  }, [item.quantity]);

  useEffect(() => {
    setAdvice(item.medicine_advice || "");
  }, [item.medicine_advice]);

  // Handle updates for debounced quantity field
  useEffect(() => {
    if (debouncedQuantity !== (item.quantity?.toString() || "0")) {
      updateMutation.mutate({
        prescriptionItemId: item.id,
        data: { quantity: parseFloat(debouncedQuantity) },
      });
    }
  }, [debouncedQuantity]);

  const handleUnitChange = (val: string) => {
    updateMutation.mutate({
      prescriptionItemId: item.id,
      data: { medicine_unit: val },
    });
  };

  const handleFrequencyChange = (val: string) => {
    updateMutation.mutate({
      prescriptionItemId: item.id,
      data: { medicine_frequency: parseInt(val) },
    });
  };

  const handleDurationChange = (val: string) => {
    updateMutation.mutate({
      prescriptionItemId: item.id,
      data: { medicine_duration: parseInt(val) },
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(item.id);
  };

  return (
    <div className="grid grid-cols-[3fr_2fr_2fr_2fr_3fr_auto] gap-4 items-center py-4 border-b border-border/60 last:border-0 hover:bg-muted/30 transition-colors px-2 rounded-lg">
      {/* Medicine Name */}
      <div>
        <div className="z-body-md text-foreground">
          {item.medicine.brand_name}
        </div>
        <div
          className="z-hint text-muted-foreground mt-0.5 line-clamp-2"
          title={item.medicine.composition}
        >
          {item.medicine.composition}
        </div>
      </div>

      {/* Quantity */}
      <div>
        <div className="flex rounded-md shadow-sm">
          <Input
            type="number"
            min="0"
            step="0.1"
            placeholder="0"
            className="h-9 w-16 rounded-r-none border-r-0 text-center px-1 focus-visible:ring-0"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={isCompleted}
          />
          <Select
            value={item.medicine_unit?.unit_id}
            onValueChange={handleUnitChange}
            disabled={isCompleted}
          >
            <SelectTrigger className="h-9 rounded-l-none bg-muted/50 border-l-0 min-w-[80px] px-2 z-caption">
              <SelectValue placeholder="Unit" />
            </SelectTrigger>
            <SelectContent>
              {units?.map((u) => (
                <SelectItem key={u.unit_id} value={u.unit_id}>
                  {u.unit_short_code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Frequency */}
      <div>
        <Select
          value={item.medicine_frequency?.id?.toString()}
          onValueChange={handleFrequencyChange}
          disabled={isCompleted}
        >
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {frequencies?.map((f) => (
              <SelectItem key={f.id} value={f.id.toString()}>
                {f.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Duration */}
      <div>
        <Select
          value={item.medicine_duration?.id?.toString()}
          onValueChange={handleDurationChange}
          disabled={isCompleted}
        >
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {durations?.map((d) => (
              <SelectItem key={d.id} value={d.id.toString()}>
                {d.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Advice — auto-growing textarea */}
      <div>
        <AdviceTextarea
          value={advice}
          onChange={setAdvice}
          onSave={(val) => {
            if (val !== (item.medicine_advice || "")) {
              updateMutation.mutate({
                prescriptionItemId: item.id,
                data: { medicine_advice: val },
              });
            }
          }}
          disabled={isCompleted}
        />
      </div>

      {/* Delete */}
      <div className="flex justify-center">
        {!isCompleted && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export const MedicineCard = ({
  appointmentId,
  isCompleted = false,
}: MedicineCardProps) => {
  const { data: prescriptions, isLoading } = usePrescriptions(appointmentId);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const activePrescription = prescriptions?.[0];
  // Filter out deleted items if API doesn't do it automatically, though usually backend handles it.
  // Assuming is_active check is needed if backend returns all.
  const prescriptionItems =
    activePrescription?.prescription_items?.filter((i) => !i.is_deleted) || [];

  return (
    <>
      <Card className="rounded-3xl border-border/60 bg-card shadow-sm">
        <CardHeader className="p-4 flex flex-row items-center justify-between border-b border-border/60">
          <div className="flex items-center gap-2">
            <Pill className="size-4 text-muted-foreground" />
            <CardTitle className="z-h3 text-foreground">
              Medicines (Rx)
            </CardTitle>
            <Info className="size-4 text-muted-foreground" />
          </div>
          {!isCompleted && (
            <Button
              size="icon"
              onClick={() => setIsAddDialogOpen(true)}
              className="h-9 w-9 rounded-xl"
            >
              <Plus className="size-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading prescriptions...
            </div>
          ) : prescriptionItems.length === 0 ? (
            <EmptyState
              icon={<Pill />}
              title="No medicines prescribed"
              subtitle="Add medicines to create a prescription"
              variant="rich"
            />
          ) : (
            <div className="space-y-2">
              {/* Table Header */}
              <div className="grid grid-cols-[3fr_2fr_2fr_2fr_3fr_auto] gap-4 px-2 pb-2 border-b border-border/60">
                <div className="z-label text-muted-foreground">
                  Medicine Name
                </div>
                <div className="z-label text-muted-foreground">Quantity</div>
                <div className="z-label text-muted-foreground">Frequency</div>
                <div className="z-label text-muted-foreground">Duration</div>
                <div className="z-label text-muted-foreground">Advice</div>
                {/* Spacer for delete button column */}
                <div className="w-7" />
              </div>

              {/* Table Body */}
              <div className="space-y-1">
                {prescriptionItems.map((item) => (
                  <PrescriptionItemRow
                    key={item.id}
                    item={item}
                    appointmentId={appointmentId}
                    isCompleted={isCompleted}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AddMedicineDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        appointmentId={appointmentId}
      />
    </>
  );
};
